import { Link, useRouterState } from "@tanstack/react-router";
import { CalendarClock } from "lucide-react";
import type { ReactNode } from "react";

import { useProPrice } from "@/app/proPrice";
import { buttonClass, Card, CardBody } from "@/components/ui";
import { cn } from "@/lib/cn";
import { formatDate } from "@/lib/format";
import type { Access } from "@/pages/account/platform";

import {
  bannerText,
  bannerTone,
  endedTitle,
  isUngated,
  payLinkShown,
  useAccess,
  useNextPrice,
  usePlanInactive,
} from "./planAccess";

/**
 * Every investor and RA sees their plan's state at the top of every page:
 * days left in the trial or paid month, amber at 7 days or less, red at 3
 * (a free trial: amber in its last 2 days), with a link to renew or to
 * continue after the trial. Not dismissible. When it has ended, only Account
 * and Payment open (PlanGate).
 */
export function PlanBanner() {
  const access = useAccess();
  const next = useNextPrice();
  if (!access || access.reason === "admin") return null;
  const tone = bannerTone(access);
  return (
    <div
      role="status"
      aria-label="Subscription"
      className={cn(
        "border-b px-4 py-1.5 text-xs sm:px-5",
        tone === "ok" && "border-border bg-brand-soft text-fg",
        tone === "warn" && "border-warn/30 bg-warn-soft text-fg",
        tone === "down" && "border-down/30 bg-down-soft text-fg",
      )}
    >
      <div className="mx-auto flex max-w-[1600px] min-w-0 items-center gap-2">
        <CalendarClock
          aria-hidden
          className={cn(
            "size-4 shrink-0",
            tone === "ok"
              ? "text-brand"
              : tone === "warn"
                ? "text-warn"
                : "text-down",
          )}
        />
        <span className="min-w-0 flex-1 font-medium sm:truncate">
          {bannerText(access, next)}
        </span>
        {payLinkShown(access) ? (
          <Link
            to="/app/payment"
            search={
              (access.kind === "ra_client"
                ? { plan: "investor_pro" }
                : {}) as never
            }
            className="shrink-0 font-medium text-brand hover:underline"
          >
            {access.kind === "ra_client"
              ? "Become a member"
              : access.kind === "trial" && access.active
                ? "Continue after trial"
                : access.kind === "complimentary"
                  ? "Pay"
                  : access.active
                    ? "Renew"
                    : "Pay now"}
          </Link>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Every page except Account and Payment, for a member whose trial or
 * membership has ended (they can still sign in, 2026-10-25): a friendly
 * renew screen with the Membership price and a Pay button.
 */
export function PlanGate({ children }: { children: ReactNode }) {
  const access = useAccess();
  const inactive = usePlanInactive();
  const path = useRouterState({ select: (s) => s.location.pathname });
  if (!inactive || isUngated(path)) return <>{children}</>;
  return <RenewScreen access={access} />;
}

function RenewScreen({ access }: { access: Access | undefined }) {
  const price = useProPrice();
  const until = access?.until ? formatDate(access.until) : null;
  return (
    <Card className="mx-auto max-w-xl" aria-label="Renew your membership">
      <CardBody className="grid justify-items-center gap-4 py-10 text-center">
        <span className="grid size-12 place-items-center rounded-full bg-brand-soft text-brand">
          <CalendarClock className="size-6" aria-hidden />
        </span>
        <div className="grid gap-1.5">
          <h2 className="font-display text-2xl leading-tight font-bold">
            {endedTitle(access?.reason)}
          </h2>
          <p className="text-sm text-muted">
            {until ? `It ended on ${until}. ` : ""}Renew to continue: your
            watchlists, alerts and saved screens are kept and open again as soon
            as your payment is confirmed.
          </p>
        </div>
        <div className="w-full max-w-sm rounded-xl border border-border bg-surface-2 px-5 py-4 text-left">
          <div className="text-xs font-semibold tracking-wide text-subtle uppercase">
            Membership
          </div>
          <div className="mt-1 font-display text-2xl font-bold">
            {price ?? "—"}
          </div>
          <p className="mt-1 text-xs text-muted">
            Every research tool: markets, stocks, screener, F&amp;O analytics,
            strategy labs, backtests, watchlists and alerts. Pay online by UPI,
            card or net banking; your plan starts at once. Nothing renews
            automatically.
          </p>
        </div>
        <div className="flex flex-wrap justify-center gap-2">
          <Link to="/app/payment" className={buttonClass("primary", "md")}>
            Pay and renew
          </Link>
          <Link to="/app/account" className={buttonClass("secondary", "md")}>
            Your account
          </Link>
        </div>
      </CardBody>
    </Card>
  );
}
